import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../common/context/AuthContext';
import { API_URL } from '../../api/config';

const SupportScreen = () => {
  const navigate = useNavigate();
  const { userToken, userData, restaurantId, signOut } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('newticket');
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [formData, setFormData] = useState({
    name: userData?.name || '',
    email: userData?.email || '',
    subject: '',
    message: '',
    priority: 'normal',
  });
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  // FAQs data
  const faqs = [
    {
      question: 'How do I reset my password?',
      answer:
        "You can reset your password by clicking the 'Forgot Password' link on the login page. Enter your email and restaurant ID, and we'll send you an OTP to reset your password.",
    },
    {
      question: 'What are your support hours?',
      answer: 'Our support team is available 24/7 to assist you with any questions or concerns.',
    },
    {
      question: 'How do I update my account information?',
      answer: "Go to Settings in your dashboard and click on 'Profile' to update your account details.",
    },
    {
      question: 'Printer is not working?',
      answer: 'Long Press on App, click on app info, go to permissions and allow Nearby devices (Remember to turn on Bluetooth and connect to printer).',
    }
  ];

  // Show notification
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      alert('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      if (!userToken) {
        throw new Error('Authentication required. Please login again.');
      }

      const response = await fetch(`${API_URL}/support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          ...formData,
          restaurantId,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to submit ticket');
      }

      showNotification('Ticket submitted successfully!', 'success');
      setFormData({
        name: userData?.name || '',
        email: userData?.email || '',
        subject: '',
        message: '',
        priority: 'normal',
      });
    } catch (error) {
      showNotification(error.message || 'An error occurred', 'error');

      if (error.message.includes('authentication') || error.message.includes('token')) {
        signOut();
        navigate('/login');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle contact actions
  const handleContactPress = (type, value) => {
    switch (type) {
      case 'email':
        window.location.href = `mailto:${value}`;
        break;
      case 'phone':
        window.location.href = `tel:${value}`;
        break;
      case 'linkedin':
        window.open('https://linkedin.com', '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-12 rounded-b-3xl text-center shadow-lg">
        <h1 className="text-3xl font-bold text-white mb-3 drop-shadow-sm">Welcome to Support</h1>
        <p className="text-xl text-white text-opacity-95">How can we assist you today?</p>
      </div>

      {/* Quick Contacts */}
      <div className="px-6 py-8 flex flex-wrap justify-between -mt-6">
        {[
          {
            icon: 'mail',
            title: 'Email Support',
            info: 'Click Here',
            action: () => handleContactPress('email', 'contact.wazwan@gmail.com'),
            gradient: 'from-red-400 to-red-500',
          },
          {
            icon: 'phone',
            title: 'Phone Support',
            info: 'Click Here',
            action: () => handleContactPress('phone', '+91 8077057743'),
            gradient: 'from-teal-400 to-teal-500',
          },
          {
            icon: 'linkedin',
            title: 'LinkedIn',
            info: 'Connect with us',
            action: () => handleContactPress('linkedin'),
            gradient: 'from-indigo-400 to-indigo-600',
          },
        ].map((item, index) => (
          <div 
            key={index}
            className="w-full sm:w-1/3 lg:w-[32%] bg-white rounded-xl p-4 mb-4 sm:mb-0 sm:mx-2 first:ml-0 last:mr-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
            onClick={item.action}
          >
            <div className={`bg-gradient-to-r ${item.gradient} w-12 h-12 rounded-full flex items-center justify-center mb-4 shadow-md`}>
              <i className={`feather-${item.icon} text-white text-xl`}></i>
            </div>
            <div className="text-center">
              <h3 className="font-bold text-gray-800 mb-1">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.info}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="mx-6 bg-white rounded-xl p-3 shadow-md flex mb-6">
        {['New Ticket', 'FAQs'].map((tab, index) => {
          const tabKey = tab.toLowerCase().replace(' ', '');
          return (
            <button
              key={index}
              className={`flex-1 flex items-center justify-center py-3 px-4 rounded-lg ${
                activeTab === tabKey 
                  ? 'bg-blue-50 text-blue-600' 
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab(tabKey)}
            >
              <i className={`feather-${tab === 'New Ticket' ? 'file-text' : 'help-circle'} mr-2 ${
                activeTab === tabKey ? 'text-blue-600' : 'text-gray-500'
              }`}></i>
              <span className={`font-medium ${activeTab === tabKey ? 'text-blue-600' : ''}`}>{tab}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="mx-6 bg-white rounded-xl p-6 shadow-md mb-6">
        {activeTab === 'newticket' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                id="name"
                type="text"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                id="email"
                type="email"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <input
                id="subject"
                type="text"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Enter subject"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                id="message"
                rows="4"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Enter your message"
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 px-6 rounded-xl font-bold text-lg shadow-lg hover:from-blue-600 hover:to-blue-700 transition-all"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                'Submit Ticket'
              )}
            </button>
          </form>
        ) : (
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-gray-50 rounded-xl p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setExpandedFaqs({ ...expandedFaqs, [index]: !expandedFaqs[index] })}
              >
                <div className="flex items-center">
                  <i className="feather-help-circle text-blue-500 text-lg mr-3"></i>
                  <h3 className="flex-1 font-medium text-gray-800">{faq.question}</h3>
                  <i className={`feather-chevron-down text-gray-600 transition-transform ${expandedFaqs[index] ? 'rotate-180' : ''}`}></i>
                </div>
                {expandedFaqs[index] && (
                  <p className="text-gray-600 mt-3 ml-8 leading-relaxed">{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification */}
      {notification.show && (
        <div className={`fixed bottom-6 left-6 right-6 py-4 px-6 rounded-xl shadow-lg text-white text-center transition-opacity ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default SupportScreen;